/**
 * 竞争对手分析数据 Hook
 */

import { useState, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { keywordSearchAPI } from '../../../../../services/api';
import type { CompetitorData, TreeNode } from '../types';
import { PRICE_RANGE_ORDER } from '../constants';
import { aggregatePriceRanges, buildPriceRangeNodes, buildPrompt } from '../utils';

export const useCompetitorData = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<CompetitorData | null>(null);
    const [showKeyword, setShowKeyword] = useState(true);
    const [showDate, setShowDate] = useState(true);
    const [showPriceRange, setShowPriceRange] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await keywordSearchAPI.competitorAnalysis();
            setData(response.data);
        } catch (error: any) {
            message.error(error.response?.data?.error || '加载失败');
        } finally {
            setLoading(false);
        }
    };

    // 全局价格区间分布
    const globalPriceRangeDist = useMemo(() => {
        if (!data?.global_stats?.price_ranges) return [];
        return PRICE_RANGE_ORDER.filter(range => data.global_stats.price_ranges[range]?.count > 0).map(range => ({
            range,
            ...data.global_stats.price_ranges[range],
        }));
    }, [data]);

    // 最佳价格区间
    const bestPriceRange = useMemo(() => {
        if (!globalPriceRangeDist.length) return null;
        return globalPriceRangeDist.reduce(
            (best, r) => ((r.sales_pct || 0) > (best?.sales_pct || 0) ? r : best),
            globalPriceRangeDist[0]
        );
    }, [globalPriceRangeDist]);

    // 构建树形数据
    const treeData: TreeNode[] = useMemo(() => {
        if (!data) return [];

        if (!showKeyword) {
            const gs = data.global_stats;
            const totalNode: TreeNode = {
                key: 'total',
                name: '全部汇总（去重）',
                nodeType: 'total',
                avg_rating: gs.avg_rating || null,
                avg_days: gs.avg_days || null,
                avg_price: gs.avg_price || null,
                brand_count: gs.unique_brands,
                total_sales: gs.total_sales || null,
                total_revenue: gs.total_revenue || null,
                result_count: gs.unique_asins,
                children: showPriceRange && gs.price_ranges ? buildPriceRangeNodes(gs.price_ranges, 'total') : undefined,
            };
            if (totalNode.children?.length === 0) totalNode.children = undefined;
            return [totalNode];
        }

        return data.keywords.map(kw => {
            let children: TreeNode[] | undefined;
            const kwPriceRanges = aggregatePriceRanges(Object.values(kw.by_date).map(d => d.price_ranges));

            if (showDate) {
                children = kw.dates.map(date => {
                    const dateData = kw.by_date[date];
                    let priceChildren: TreeNode[] | undefined;
                    if (showPriceRange && dateData.price_ranges) {
                        priceChildren = buildPriceRangeNodes(dateData.price_ranges, `${kw.keyword}_${date}`);
                        if (priceChildren.length === 0) priceChildren = undefined;
                    }
                    return {
                        key: `${kw.keyword}_${date}`,
                        name: date,
                        nodeType: 'date' as const,
                        avg_rating: dateData.rating,
                        avg_days: dateData.days,
                        avg_price: dateData.price,
                        brand_count: dateData.brand_count,
                        total_sales: dateData.sales,
                        total_revenue: dateData.revenue,
                        result_count: 0,
                        children: priceChildren,
                    };
                });
            } else if (showPriceRange) {
                children = buildPriceRangeNodes(kwPriceRanges, kw.keyword);
            }

            if (children?.length === 0) children = undefined;

            return {
                key: kw.keyword,
                name: kw.keyword,
                nodeType: 'keyword' as const,
                avg_rating: kw.avg_rating,
                avg_days: kw.avg_days,
                avg_price: kw.avg_price,
                brand_count: kw.brand_count,
                total_sales: kw.total_sales,
                total_revenue: kw.total_revenue,
                result_count: kw.total_results,
                children,
            };
        });
    }, [data, showKeyword, showDate, showPriceRange]);

    return {
        loading,
        data,
        showKeyword,
        setShowKeyword,
        showDate,
        setShowDate,
        showPriceRange,
        setShowPriceRange,
        globalPriceRangeDist,
        bestPriceRange,
        treeData,
        buildPrompt,
    };
};
